import React, { useState } from 'react';
import { Bitcoin, Scale, TrendingUp, Loader2 } from 'lucide-react';
import { formatCurrency } from '../utils/formatters';
import { GoldPrice } from '../types';

interface PriceCardProps {
  bitcoinPrice: number | null;
  goldAmount: GoldPrice | null;
  goldPricePerGram: number | null;
  goldLastUpdated: number | null;
  isLoading?: boolean;
}

function formatTimeAgo(timestamp: number): string {
  const now = Date.now();
  const diffInSeconds = Math.floor((now - timestamp) / 1000);
  
  if (diffInSeconds < 60) {
    return 'just now';
  } else if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60);
    return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`;
  } else if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600);
    return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
  } else {
    const days = Math.floor(diffInSeconds / 86400);
    return `${days} ${days === 1 ? 'day' : 'days'} ago`;
  }
}

const kgToLbs = (kg: number) => kg * 2.20462;

export const PriceCard: React.FC<PriceCardProps> = ({ 
  bitcoinPrice, 
  goldAmount,
  goldPricePerGram,
  goldLastUpdated,
  isLoading = false
}) => {
  const [showLbs, setShowLbs] = useState(false);
  
  const LoadingSpinner = () => (
    <div className="flex items-center justify-center py-8">
      <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
    </div>
  );

  const isDevelopment = import.meta.env.DEV;

  const formatWeight = (kilos: number) => {
    if (showLbs) {
      return `${kgToLbs(kilos).toFixed(2)}`;
    }
    return `${kilos.toFixed(2)}`;
  };

  return (
    <div className="col-span-2 bg-white rounded-xl p-8 shadow-lg transform hover:shadow-xl transition-all duration-300">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Bitcoin Section */}
        <div className="border-b md:border-b-0 md:border-r border-gray-200 pb-6 md:pb-0 md:pr-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Bitcoin className="w-10 h-10 text-orange-500" />
              <h2 className="text-2xl font-bold">Bitcoin</h2>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                <span className="text-xs text-green-600 font-medium">LIVE</span>
              </div>
              <TrendingUp className="w-6 h-6 text-green-500" />
            </div>
          </div>
          {isLoading ? <LoadingSpinner /> : (
            <div>
              <p className="text-5xl font-bold text-orange-500">
                {bitcoinPrice ? formatCurrency(bitcoinPrice) : 'N/A'}
              </p>
              <p className="text-gray-500 text-sm mt-1">Price of 1 Bitcoin</p>
            </div>
          )}
        </div>

        {/* Gold Equivalent Section */}
        <div className="pt-6 md:pt-0 md:pl-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Scale className="w-10 h-10 text-yellow-500" />
              <h2 className="text-2xl font-bold">Gold Equivalent</h2>
            </div>
            <div className="flex flex-col items-end gap-1">
              <div className="flex items-center gap-2">
                {isDevelopment && (
                  <span className="text-xs px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full">Mock</span>
                )}
                <p className="text-sm text-gray-500">
                  {goldPricePerGram ? formatCurrency(goldPricePerGram) : 'N/A'}/g
                </p>
              </div>
              {goldLastUpdated && (
                <p className="text-xs text-gray-400">
                  Updated {formatTimeAgo(goldLastUpdated)}
                </p>
              )}
            </div>
          </div>
          {isLoading ? <LoadingSpinner /> : (
            <div>
              <div className="flex items-baseline gap-2">
                <p className="text-5xl font-bold text-yellow-500">
                  {goldAmount ? formatWeight(goldAmount.kilos) : 'N/A'}
                </p>
                <div className="flex items-center gap-1 text-lg font-medium">
                  {showLbs ? (
                    <>
                      <button 
                        onClick={() => setShowLbs(true)}
                        className="text-yellow-500 opacity-100 text-4xl transition-all duration-300"
                      >
                        lbs
                      </button>
                      <span className="text-gray-400">/</span>
                      <button 
                        onClick={() => setShowLbs(false)}
                        className="text-gray-400 opacity-50 hover:opacity-75 transition-all duration-300"
                      >
                        kg
                      </button>
                    </>
                  ) : (
                    <>
                      <button 
                        onClick={() => setShowLbs(false)}
                        className="text-yellow-500 opacity-100 text-4xl transition-all duration-300"
                      >
                        kg
                      </button>
                      <span className="text-gray-400">/</span>
                      <button 
                        onClick={() => setShowLbs(true)}
                        className="text-gray-400 opacity-50 hover:opacity-75 transition-all duration-300"
                      >
                        lbs
                      </button>
                    </>
                  )}
                </div>
              </div>
              <p className="text-gray-500 text-sm mt-1">of Gold</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};